/**
 * The shared parts of the ticket question ("have a ticket?", TECH-575). Each host composes its own
 * frame around them: the ticketed Going celebration (`TicketedGoingCelebrationDrawer`) and the
 * Going PIN sheet's last step (`EventPinTicketStep`) each write their own title and keep their own
 * extras (the celebration's eyebrow, illustration and footnote; the sheet's chip).
 *
 * - `useTicketQuestion(...)` (`./useTicketQuestion`) fires the view analytics once and returns a
 *   `TicketQuestion` handle (registration facts plus the `answer` handler). Call it once per
 *   question, in the host.
 * - `TicketQuestionText` is the text under the host's title; the host words its opening line
 *   before the event (`upcomingLead`, built around `TicketSite`), so it follows that title.
 *   `TicketQuestionAnswers` is both answers. Render the text in the scrolling area and the answers
 *   in a pinned footer (or last in a `CelebrationDrawerFrame`), passing both the same handle.
 */
import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { DrawerDescription } from '@components/ui/drawer';
import { Button } from '@components/ui/button';
import { ImgWithFallback } from '@components/ui/img-with-fallback';
import { getFaviconUrl } from '@utils/favicon';
import { openInSystemBrowserInBackground } from '@utils/shareHelpers';
import type { TicketQuestion } from './useTicketQuestion';

/**
 * Both answers: identical, so neither reads as the preferred one. The focus ring is mb-teal-ink with
 * an offset, as every Button's is: without it the ring would sit on the teal border at 2.52:1
 * (ui-ux-designer, WCAG 2.2 SC 1.4.11).
 */
const ANSWER_BUTTON_CLASS =
  'h-13 w-full gap-2 rounded-full border-2 border-mb-teal bg-white font-fira text-base font-medium text-mb-teal-ink hover:bg-mb-teal-pale hover:text-mb-teal-ink active:bg-mb-teal-pale focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2';

/** The ticket site's icon, then its domain in bold. The icon is decoration: the domain is the
 * text, so a failed favicon just disappears. The icon is looked up from the link itself, not from
 * `domain`, which is the display text with its "www." stripped. A domain has no break opportunity
 * and is unbounded (the whole registration_url when it has no host), so it breaks anywhere rather
 * than overflow. */
export const TicketSite: React.FC<{ domain: string; url: string }> = ({ domain, url }) => (
  <>
    <ImgWithFallback
      src={getFaviconUrl(url)}
      alt=""
      className="mr-1 inline-block size-4.5 rounded-full align-text-bottom"
      fallback={() => null}
    />
    <span className="font-bold text-mb-teal-ink wrap-anywhere">{domain}</span>
  </>
);

interface TicketQuestionPartProps {
  question: TicketQuestion;
}

interface TicketQuestionTextProps extends TicketQuestionPartProps {
  /**
   * Before the event, the line that names the ticket site (built around `TicketSite`), worded by
   * the host to follow its own title: under "This event requires a ticket", where to get one;
   * under "Got your ticket?", where the event is ticketed, since an order there ("Get your
   * ticket") answers the question with "no". While the event is live, both say the door line.
   */
  upcomingLead: React.ReactNode;
}

/** The text under the host's title: where the ticket is, and (before the event) why come anyway. */
export const TicketQuestionText: React.FC<TicketQuestionTextProps> = ({
  question,
  upcomingLead,
}) => {
  const { registration, domain, live } = question;
  const { registration_url } = registration;
  return (
    <DrawerDescription asChild>
      <div className="flex flex-col gap-2.5 font-fira text-sm text-mb-dark-gray">
        {live ? (
          <p>
            This event is ticketed on <TicketSite domain={domain} url={registration_url} />.
            You&apos;ll need a ticket at the door.
          </p>
        ) : (
          <>
            <p>{upcomingLead}</p>
            <p>
              The ticket gets you through the door. meetball helps you find the people you came to
              meet.
            </p>
          </>
        )}
      </div>
    </DrawerDescription>
  );
};

/** Both answers, in one column. */
export const TicketQuestionAnswers: React.FC<TicketQuestionPartProps> = ({ question }) => {
  const { registration, domain, answer, trackLinkClicked } = question;
  const { registration_url } = registration;
  return (
    <div className="flex flex-col gap-3">
      <Button
        type="button"
        variant="outline"
        className={ANSWER_BUTTON_CLASS}
        onClick={() => {
          answer('registered');
        }}
      >
        I already have a ticket
      </Button>
      {/* A real link, so it reads as one to assistive tech, but the click goes through
          openInSystemBrowserInBackground, so native opens the system browser, where the member
          is signed in to the ticket site (a bare target="_blank" is a no-op in the webview).
          Answered, then opened, synchronously: an await between the tap and the open makes iOS
          Safari block the popup, so the PUT is fire-and-forget. The answer goes first because
          the host may refuse the tap (its entrance veto), and a refused tap must not open the
          ticket site. The name says which site it opens, not where: a tab on web, the system
          browser on native. */}
      <Button asChild variant="outline" className={ANSWER_BUTTON_CLASS}>
        <a
          href={registration_url}
          target="_blank"
          rel="noopener"
          aria-label={`I need a ticket, opens ${domain}`}
          onClick={e => {
            e.preventDefault();
            if (!answer('not_registered')) return;
            trackLinkClicked();
            openInSystemBrowserInBackground(registration_url);
          }}
        >
          I need a ticket
          <ArrowUpRight aria-hidden="true" />
        </a>
      </Button>
    </div>
  );
};
